import { useRef } from 'react';
import { useQuery } from 'react-query';
import { useFilterToolbarState } from 'hooks/filterToolbar/useFilterToolbarState';
import { useAreGroupFiltersReady } from 'hooks/projectMap/useAreGroupFiltersReady';
import { useScoutbotSavedSearchQueries } from 'hooks/projectMap/useScoutbotSavedSearchQueries';
import { fetchMapPins } from 'pages/ProjectInsights/services';
import { useSelectedPolygonBoundsOnMap } from 'state/map';
import { useMapBoundsAsCardinalDirections } from 'state/mapSettings';
import { useZoomQuery } from 'state/queryParams';
import { IdentifiableCoords } from 'types/map';
import { ZOOM_THRESHOLD } from 'utils/constants';

type Marker = IdentifiableCoords & { address: string };

export const useProjectMapPins = () => {
  const { filterFieldsState, getFiltersAsQueryParams, getMapStateAsQueryParams } =
    useFilterToolbarState();
  const [zoomQuery] = useZoomQuery();
  const mapMarkerAbortControllerRef = useRef(new AbortController());
  const [mapBoundsAsCardinalDirections] = useMapBoundsAsCardinalDirections();
  const [selectedPolygonBoundsOnMap] = useSelectedPolygonBoundsOnMap();
  const { isScoutSearch, rawQueries } = useScoutbotSavedSearchQueries();
  const areGroupFiltersReady = useAreGroupFiltersReady();

  const mapMarkerResponse = useQuery({
    queryKey: [
      'map-search-pins',
      filterFieldsState,
      mapBoundsAsCardinalDirections,
      selectedPolygonBoundsOnMap,
      isScoutSearch,
      rawQueries,
    ],
    queryFn: async () => {
      mapMarkerAbortControllerRef.current.abort();
      mapMarkerAbortControllerRef.current = new AbortController();

      const mapState = getMapStateAsQueryParams();

      if (selectedPolygonBoundsOnMap) {
        mapState.pathOrBounds = selectedPolygonBoundsOnMap;
      }

      const allFilters = getFiltersAsQueryParams();
      // A scoutbot search's real filters live in raw_queries - the
      // individual filter fields stay empty, so only the date/status fields
      // (which the raw-queries path also applies) still apply on top of it.
      const filterState = isScoutSearch
        ? {
            raw_queries: rawQueries,
            startDate: allFilters.startDate,
            createdAtStart: allFilters.createdAtStart,
            createdAtEnd: allFilters.createdAtEnd,
            status: allFilters.status,
          }
        : allFilters;

      const mapPins = await fetchMapPins({
        ...filterState,
        ...mapState,
        signal: mapMarkerAbortControllerRef.current.signal,
      });

      return (
        mapPins
          ?.map<Marker>(({ id, coordinates, address: a }) => ({
            id: id.toString(),
            address: a,
            lat: coordinates[1],
            lng: coordinates[0],
          }))
          .filter((v, i, a) => a.findIndex((item) => item.address === v.address) === i) ||
        []
      );
    },
    enabled:
      Number(zoomQuery) >= ZOOM_THRESHOLD &&
      (!isScoutSearch || !!rawQueries) &&
      areGroupFiltersReady,
    refetchOnWindowFocus: false,
    keepPreviousData: true,
  });

  return {
    isLoading: mapMarkerResponse.isLoading,
    mapMarkers: mapMarkerResponse.data || [],
  };
};
